import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { COMPANY } from "../../data/site";
import hazLogo from "../../assets/haz-logo.png";

const NAV = [
    { label: "About", to: "/about" },
    { label: "Services", to: "/services" },
    { label: "Service Areas", to: "/service-areas" },
    { label: "Reviews", to: "/reviews" },
    { label: "Contractors", to: "/contractors" },
    { label: "Contact", to: "/contact" },
];

export const Header = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [pathname]);

    return (
        <header
            data-testid="site-header"
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-bone/90 backdrop-blur-xl border-b border-line/70"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-24 md:h-28">
                <Link
                    to="/"
                    data-testid="logo-home-link"
                    className="flex items-center gap-3 group"
                    aria-label="HAZ Insulation — home"
                >
                    <img
                        src={hazLogo}
                        alt="HAZ Insulation"
                        className="h-[3.84rem] md:h-[5.12rem] w-auto"
                    />
                </Link>

                <nav className="hidden lg:flex items-center gap-8">
                    {NAV.map((n) => (
                        <Link
                            key={n.label}
                            to={n.to}
                            data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                            className="link-underline text-sm font-medium text-ink hover:text-copper"
                        >
                            {n.label}
                        </Link>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-4">
                    <a
                        href={COMPANY.phoneHref}
                        data-testid="header-phone-link"
                        className="flex items-center gap-2 text-sm font-semibold text-ink hover:text-copper"
                    >
                        <Phone size={16} strokeWidth={1.75} />
                        {COMPANY.phone}
                    </a>
                    <Link
                        to="/contact"
                        data-testid="header-quote-cta"
                        className="btn-lift bg-copper text-bone font-semibold py-3 px-6 hover:bg-copper-hover"
                    >
                        Free Quote
                    </Link>
                </div>

                <button
                    onClick={() => setOpen((v) => !v)}
                    data-testid="mobile-menu-toggle"
                    className="lg:hidden p-2 text-ink"
                    aria-label="Toggle menu"
                >
                    {open ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {open && (
                <div
                    data-testid="mobile-menu"
                    className="lg:hidden bg-bone border-t border-line"
                >
                    <div className="px-6 py-6 flex flex-col gap-5">
                        {NAV.map((n) => (
                            <Link
                                key={n.label}
                                to={n.to}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                                className="text-base font-medium text-ink"
                            >
                                {n.label}
                            </Link>
                        ))}
                        <a
                            href={COMPANY.phoneHref}
                            onClick={() => setOpen(false)}
                            className="flex items-center gap-2 text-sm font-semibold text-moss"
                            data-testid="mobile-phone-link"
                        >
                            <Phone size={16} /> {COMPANY.phone}
                        </a>
                        <Link
                            to="/contact"
                            onClick={() => setOpen(false)}
                            data-testid="mobile-quote-cta"
                            className="bg-copper text-bone text-center font-semibold py-3 px-6"
                        >
                            Free Quote
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
};
